import { ConnectProto, JobType, jobTypeToNumber } from "@racoon/api";
import { getEnumName } from "@racoon/lib";
import { DraftJob, makeDraftJob } from "~/features/assign-route/model/draft-jobs";
import { getJobCategoryForJobType } from "~/pages/dashboard/routes/hooks/msw-route-list/jobCategoryRouteCompatibility";

/**
 * One (container group × job type) pair picked in the "add jobs" modal — the
 * same unit a pickup location group's links are made of, and what one stop of
 * the pseudo-route turns into.
 */
export interface PseudoJobPick {
  containerGroup: ConnectProto.ContainerGroup;
  /** Resolved from the locations list; the container group itself carries only its id. */
  pickupLocation?: ConnectProto.PickupLocation;
  /** "JOB_TYPE_*" — the string form the container group's contract is keyed by. */
  jobType: string;
}

/**
 * A stop of the route being assembled: either a job the backend will create with
 * the route, or one that already exists and is only being attached.
 */
export type PseudoStop =
  | ({ kind: "create" } & DraftJob)
  | { kind: "existing"; tempId: number; job: ConnectProto.Job };

/** Identity of a pick, so the same pair can't be added to the route twice. */
export const pseudoJobKey = (containerGroupId: bigint | string, jobType: string) =>
  `${containerGroupId}|${jobType}`;

/** The (container group × job type) pair a stop occupies, if it names one. */
export const pseudoStopKey = (stop: PseudoStop): string | null => {
  const containerGroupId =
    stop.kind === "create" ? stop.create.containerGroupId : stop.job.containerGroup?.id;
  const type = stop.kind === "create" ? stop.create.type : stop.job.type;
  const jobType = type == null ? undefined : getEnumName(ConnectProto.JobTypeSchema, type);
  return containerGroupId && jobType ? pseudoJobKey(containerGroupId, jobType) : null;
};

interface BuildPseudoStopsArgs {
  picks: PseudoJobPick[];
  /**
   * First synthetic index to hand out. Ids must stay unique for the life of the
   * modal, so the caller keeps a counter that only ever grows — reusing indices
   * of removed rows would collide with stops still in the list.
   */
  startIndex: number;
  serviceProviderId?: bigint;
  authorId?: bigint;
}

/**
 * Turns the modal's picks into stops the dispatcher can order, drop and see on
 * the map before anything exists on the server.
 */
export const buildPseudoStops = ({
  picks,
  startIndex,
  serviceProviderId,
  authorId,
}: BuildPseudoStopsArgs): PseudoStop[] =>
  picks.map(({ containerGroup, pickupLocation, jobType }, offset) => {
    const type = jobTypeToNumber(jobType as JobType) as ConnectProto.JobType;

    return {
      kind: "create" as const,
      ...makeDraftJob({
        index: startIndex + offset,
        pickupLocation,
        containerGroup,
        // The container group's owner is the customer the work is done for.
        serviceReceiver: containerGroup.ownerOrganization,
        create: {
          pickupLocationId: containerGroup.pickupLocationId,
          type,
          jobCategory: getJobCategoryForJobType(type),
          containerGroupId: containerGroup.id,
          containerTypeId: containerGroup.containerType?.id,
          serviceProviderId,
          serviceReceiverId: containerGroup.ownerOrganizationId,
          authorId,
        },
      }),
    };
  });

/**
 * Wraps already-created jobs as stops. They keep their real record, and like
 * drafts they are addressed by a synthetic id so the list, map and volume maths
 * need no second code path.
 */
export const buildExistingStops = (jobs: ConnectProto.Job[], startIndex: number): PseudoStop[] =>
  jobs.map((job, offset) => ({
    kind: "existing",
    tempId: -(startIndex + offset + 1),
    job,
  }));
